"use client";

import React, { useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputWithPicker, TextareaWithPicker } from '@/components/forms/FieldWithPicker';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, FolderPlus } from 'lucide-react';
import { ListFormData } from '@/types/messages';
import {
  containsDisallowedCharOutsideSpintax,
  stripDisallowedOutsideSpintax,
} from '@/utils/spintax';

const LIST_DISALLOWED = '/|*';
const stripListDisallowed = (value: string) =>
  stripDisallowedOutsideSpintax(value, LIST_DISALLOWED);
const hasInvalidListChars = (value: string) =>
  containsDisallowedCharOutsideSpintax(value, LIST_DISALLOWED);
const invalidListCharsMessage =
  'Cannot include /, *, or | outside of spintax blocks like {Hi|Hello}';

const listFormSchema = z.object({
  title: z.string().optional().refine((v) => !v || !hasInvalidListChars(v), invalidListCharsMessage),
  description: z
    .string()
    .min(1, 'Description is required')
    .refine((v) => !hasInvalidListChars(v), invalidListCharsMessage),
  footer: z.string().optional().refine((v) => !v || !hasInvalidListChars(v), invalidListCharsMessage),
  buttonText: z.string().optional().refine((v) => !v || !hasInvalidListChars(v), invalidListCharsMessage),
  sections: z
    .array(
      z.object({
        id: z.string(),
        name: z
          .string()
          .min(1, 'Section name is required')
          .refine((v) => !hasInvalidListChars(v), invalidListCharsMessage),
        items: z
          .array(
            z.object({
              id: z.string(),
              title: z
                .string()
                .min(1, 'Item title is required')
                .refine((v) => !hasInvalidListChars(v), invalidListCharsMessage),
              description: z
                .string()
                .optional()
                .refine((v) => !v || !hasInvalidListChars(v), invalidListCharsMessage),
              optionId: z
                .string()
                .min(1, 'Option ID is required')
                .refine((v) => !hasInvalidListChars(v), invalidListCharsMessage),
            })
          )
          .min(1, 'Each section must have at least one item'),
      })
    )
    .min(1, 'At least one section is required'),
});

interface ImprovedListMessageFormProps {
  onFormChange: (data: ListFormData) => void;
  onGenerate: (message: string) => void;
  initialValues?: ListFormData;
}

export function ImprovedListMessageForm({ onFormChange, onGenerate, initialValues }: ImprovedListMessageFormProps) {
  const { t } = useTranslation();
  const onFormChangeRef = useRef(onFormChange);
  onFormChangeRef.current = onFormChange;
  const isResettingRef = React.useRef(false);

  const form = useForm<ListFormData>({
    resolver: zodResolver(listFormSchema),
    defaultValues: initialValues ?? {
      title: '',
      description: '',
      footer: '',
      buttonText: '',
      sections: [
        {
          id: 'section-1',
          name: '',
          items: [
            {
              id: 'item-1',
              title: '',
              description: '',
              optionId: '',
            },
          ],
        },
      ],
    },
  });

  // Reset when initialValues change (only when different) and avoid feedback loop
  React.useEffect(() => {
    if (!initialValues) return;
    const current = form.getValues();
    const same = JSON.stringify(current) === JSON.stringify(initialValues);
    if (!same) {
      isResettingRef.current = true;
      form.reset(initialValues);
      queueMicrotask(() => {
        isResettingRef.current = false;
      });
    }
  }, [initialValues, form]);

  const { fields: sectionFields, append: appendSection, remove: removeSection } = useFieldArray({
    control: form.control,
    name: 'sections',
  });

  const watchedValues = form.watch();

  // Update parent component when form changes
  React.useEffect(() => {
    const subscription = form.watch((value) => {
      if (isResettingRef.current) return;
      onFormChangeRef.current(value as ListFormData);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = (data: ListFormData) => {
    // Dynamic import to avoid require
    import('@/utils/messageGenerator').then(({ generateListMessage }) => {
      const message = generateListMessage(data);
      onGenerate(message);
    });
  };

  const addSection = () => {
    const newSectionId = `section-${Date.now()}`;
    const newItemId = `item-${Date.now()}`;
    appendSection({
      id: newSectionId,
      name: '',
      items: [
        {
          id: newItemId,
          title: '',
          description: '',
          optionId: '',
        },
      ],
    });
  };

  const addItemToSection = (sectionIndex: number) => {
    const newItemId = `item-${Date.now()}`;
    const currentItems = form.getValues(`sections.${sectionIndex}.items`);
    form.setValue(`sections.${sectionIndex}.items`, [
      ...currentItems,
      {
        id: newItemId,
        title: '',
        description: '',
        optionId: '',
      },
    ]);
  };

  const removeItemFromSection = (sectionIndex: number, itemIndex: number) => {
    const currentItems = form.getValues(`sections.${sectionIndex}.items`);
    if (currentItems.length > 1) {
      form.setValue(`sections.${sectionIndex}.items`, 
        currentItems.filter((_, i) => i !== itemIndex)
      );
    }
  };

  const getTotalItems = () => {
    return watchedValues.sections.reduce((total, section) => total + section.items.length, 0);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('listMessage.title')}</CardTitle>
        <CardDescription>
          {t('listMessage.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                <FormItem>
                    <FormLabel>{t('listMessage.form.title')} <span className="text-gray-400">{t('listMessage.form.titleOptional')}</span></FormLabel>
                    <FormControl>
                      <InputWithPicker placeholder={t('placeholders.title')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="buttonText"
                render={({ field }) => (
                <FormItem>
                    <FormLabel>{t('listMessage.form.buttonText')} <span className="text-gray-400">{t('common.optional')}</span></FormLabel>
                    <FormControl>
                      <InputWithPicker placeholder={t('listMessage.form.buttonTextPlaceholder')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
              <FormItem>
                <FormLabel>{t('listMessage.form.description')} <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <TextareaWithPicker 
                    placeholder={t('placeholders.description')}
                    {...field}
                    onChange={(e) => field.onChange(stripListDisallowed(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="footer"
              render={({ field }) => (
              <FormItem>
                <FormLabel>{t('listMessage.form.footer')} <span className="text-gray-400">{t('common.optional')}</span></FormLabel>
                <FormControl>
                  <TextareaWithPicker
                    placeholder={t('placeholders.footer')}
                    className="min-h-[80px]"
                    {...field}
                    onChange={(e) => field.onChange(stripListDisallowed(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              )}
            />

            <Separator />

            {/* Sections */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium">{t('listMessage.form.sections')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('listMessage.form.sectionsDescription')}
                  </p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {sectionFields.length} {t('listMessage.form.section')}{sectionFields.length !== 1 ? 's' : ''} • {getTotalItems()} {t('listMessage.form.totalItems')}
                </div>
              </div>

              <div className="space-y-6">
                {sectionFields.map((sectionField, sectionIndex) => {
                  const sectionItems = form.watch(`sections.${sectionIndex}.items`);
                  
                  return (
                    <Card key={sectionField.id} className="p-4 border-2 border-dashed border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <FolderPlus className="w-5 h-5 text-blue-500" />
                          <h4 className="text-md font-medium">{t('listMessage.form.section')} {sectionIndex + 1}</h4>
                          <Badge variant="outline">
                            {sectionItems.length} {t('listMessage.form.item')}{sectionItems.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        {sectionFields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSection(sectionIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {/* Section Name */}
                      <div className="mb-4">
                        <FormField
                          control={form.control}
                          name={`sections.${sectionIndex}.name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('listMessage.form.sectionName')} <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <InputWithPicker placeholder={t('placeholders.sectionName')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Section Items */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-medium text-gray-700">{t('listMessage.form.items')}</h5>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addItemToSection(sectionIndex)}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            {t('listMessage.form.addItem')}
                          </Button>
                        </div>

                        {sectionItems.map((itemField: { id: string }, itemIndex: number) => (
                          <Card key={itemField.id} className="p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-600">
                                {t('listMessage.form.item')} {itemIndex + 1}
                              </span>
                              {sectionItems.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItemFromSection(sectionIndex, itemIndex)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <FormField
                                control={form.control}
                                name={`sections.${sectionIndex}.items.${itemIndex}.title`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('listMessage.form.itemTitle')} <span className="text-red-500">*</span></FormLabel>
                                    <FormControl>
                                      <InputWithPicker placeholder={t('placeholders.itemTitle')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`sections.${sectionIndex}.items.${itemIndex}.optionId`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('listMessage.form.itemId')} <span className="text-red-500">*</span></FormLabel>
                                    <FormControl>
                                      <Input placeholder={t('placeholders.itemId')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`sections.${sectionIndex}.items.${itemIndex}.description`}
                                render={({ field }) => (
                                  <FormItem className="md:col-span-2">
                                    <FormLabel>{t('listMessage.form.itemDescription')} <span className="text-gray-400">{t('common.optional')}</span></FormLabel>
                                    <FormControl>
                                      <InputWithPicker placeholder={t('placeholders.itemDescription')} {...field} onChange={(e) => field.onChange(stripListDisallowed(e.target.value))} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </Card>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Add Section Button */}
              <div className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addSection}
                  className="w-full"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  {t('listMessage.form.addSection')}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}